import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';

export default function BankDetails() {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const { currentUser } = useAuth();
    const [bankDetails, setBankDetails] = useState({
        bankName: '',
        accountNumber: '',
        confirmAccountNumber: '',
        ifscCode: '',
        accountType: ''
    });
    const [showAccountNumber, setShowAccountNumber] = useState(false);
    const [showConfirmAccountNumber, setShowConfirmAccountNumber] = useState(false);
    const [accounts, setAccounts] = useState([]);
    const [primaryId, setPrimaryId] = useState(null);

    // Fetch all bank accounts for the user
    useEffect(() => {
        const fetchAccounts = async () => {
            if (!currentUser) return;
            const querySnapshot = await getDocs(collection(db, 'users', currentUser.uid, 'banks'));
            const accs = [];
            let primary = null;
            querySnapshot.forEach(docSnap => {
                const data = docSnap.data();
                accs.push({ id: docSnap.id, ...data });
                if (data.isPrimary) primary = docSnap.id;
            });
            setAccounts(accs);
            setPrimaryId(primary);
        };
        fetchAccounts();
    }, [currentUser, message]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        // Only allow numbers for account fields
        if (name === 'accountNumber' || name === 'confirmAccountNumber') {
            if (!/^\d*$/.test(value)) return;
        }
        setBankDetails(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setError('');
        setMessage('');

        // Validation: account numbers must match and be at least 6 digits
        if (
            !bankDetails.accountNumber ||
            !bankDetails.confirmAccountNumber ||
            bankDetails.accountNumber !== bankDetails.confirmAccountNumber
        ) {
            setError('Account numbers do not match.');
            return;
        }
        if (bankDetails.accountNumber.length < 6) {
            setError('Account number must be at least 6 digits.');
            return;
        }
        if (!bankDetails.ifscCode.match(/^[A-Za-z]{4}\d{7}$/)) {
            setError('Invalid IFSC code format.');
            return;
        }

        try {
            setLoading(true);
            // If this is the first account, set as primary
            const isPrimary = accounts.length === 0;
            await addDoc(collection(db, 'users', currentUser.uid, 'banks'), {
                bankName: bankDetails.bankName,
                accountNumber: bankDetails.accountNumber,
                ifscCode: bankDetails.ifscCode,
                accountType: bankDetails.accountType,
                isPrimary,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });

            setMessage('Bank details saved successfully');
            setBankDetails({
                bankName: '',
                accountNumber: '',
                confirmAccountNumber: '',
                ifscCode: '',
                accountType: ''
            });
        } catch (err) {
            setError('Failed to save bank details: ' + err.message);
        }
        setLoading(false);
    };

    // Helper to mask account number except last 2 digits
    const maskAccountNumber = (value) => {
        if (!value) return '';
        const len = value.length;
        if (len <= 2) return '*'.repeat(len);
        return '*'.repeat(len - 2) + value.slice(-2);
    };

    // Set a bank account as primary
    const handleSetPrimary = async (id) => {
        if (!currentUser) return;
        setLoading(true);
        try {
            // Set isPrimary: false for all accounts, then true for selected
            await Promise.all(
                accounts.map(acc =>
                    updateDoc(
                        doc(db, 'users', currentUser.uid, 'banks', acc.id),
                        { isPrimary: acc.id === id }
                    )
                )
            );
            setMessage('Primary account updated.');
        } catch (err) {
            setError('Failed to update primary account.');
        }
        setLoading(false);
    };

    // // Delete a bank account
    // const handleDelete = async (id) => {
    //     if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    //     try {
    //         await deleteDoc(doc(db, 'users', currentUser.uid, 'banks', id));
    //         setMessage('Bank account deleted.');
    //     } catch (err) {
    //         setError('Failed to delete bank account.');
    //     }
    // };

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#e0e7ff] via-white to-[#f0f5ff] py-8 flex items-center justify-center">
            <div className="w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-500 to-indigo-700 px-4 py-6 text-center relative">
                    <h2 className="text-3xl font-extrabold text-white mb-1">Bank Account Details</h2>
                </div>
                <form onSubmit={handleSubmit} className="p-6 sm:p-10">
                    {error && <div className="mb-4 text-red-700 bg-red-100 border border-red-200 rounded-lg px-4 py-2">{error}</div>}
                    {message && <div className="mb-4 text-green-800 bg-green-100 border border-green-200 rounded-lg px-4 py-2">{message}</div>}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                        <div className="sm:col-span-2">
                            <label className="block text-sm font-semibold mb-1 text-gray-700">Bank Name</label>
                            <input
                                type="text"
                                name="bankName"
                                value={bankDetails.bankName}
                                onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                                required
                                placeholder="Enter bank name"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1 text-gray-700">Account Number</label>
                            <input
                                type={showAccountNumber ? "text" : "password"}
                                name="accountNumber"
                                value={showAccountNumber ? bankDetails.accountNumber : maskAccountNumber(bankDetails.accountNumber)}
                                onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                                required
                                placeholder="Enter account number"
                                maxLength={20}
                                autoComplete="off"
                                onFocus={() => setShowAccountNumber(true)}
                                onBlur={() => setShowAccountNumber(false)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1 text-gray-700">Re-enter Account Number</label>
                            <input
                                type={showConfirmAccountNumber ? "text" : "password"}
                                name="confirmAccountNumber"
                                value={showConfirmAccountNumber ? bankDetails.confirmAccountNumber : maskAccountNumber(bankDetails.confirmAccountNumber)}
                                onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                                required
                                placeholder="Re-enter account number"
                                maxLength={20}
                                autoComplete="off"
                                onFocus={() => setShowConfirmAccountNumber(true)}
                                onBlur={() => setShowConfirmAccountNumber(false)}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1 text-gray-700">IFSC Code</label>
                            <input
                                type="text"
                                name="ifscCode"
                                value={bankDetails.ifscCode}
                                onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50 uppercase"
                                required
                                placeholder="Enter IFSC code"
                                maxLength={11}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold mb-1 text-gray-700">Account Type</label>
                            <select
                                name="accountType"
                                value={bankDetails.accountType}
                                onChange={handleChange}
                                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-gray-50"
                                required
                            >
                                <option value="">Select account type</option>
                                <option value="savings">Savings</option>
                                <option value="current">Current</option>
                                <option value="nre">NRE</option>
                                <option value="nro">NRO</option>
                            </select>
                        </div>
                    </div>
                    <div className="flex justify-center mt-10">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`w-full sm:w-1/2 bg-gradient-to-r from-indigo-500 to-indigo-700 text-white py-3 rounded-xl font-semibold shadow-lg hover:from-indigo-600 hover:to-indigo-800 transition-all duration-200 ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <svg className="animate-spin h-5 w-5 mr-2 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                    </svg>
                                    Saving...
                                </span>
                            ) : (
                                'Add Bank Account'
                            )}
                        </button>
                    </div>
                </form>
                {/* List of saved bank accounts */}
                <div className="p-6">
                    <h3 className="text-lg font-semibold mb-4 text-indigo-700">Your Bank Accounts</h3>
                    {accounts.length === 0 ? (
                        <div className="text-gray-500">No bank accounts added yet.</div>
                    ) : (
                        <ul className="divide-y divide-gray-200">
                            {accounts.map(acc => (
                                <li key={acc.id} className="py-3 flex items-center justify-between">
                                    <div>
                                        <div className="font-semibold">
                                            {acc.bankName}
                                            {acc.isPrimary && (
                                                <span className="ml-2 px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full font-semibold align-middle">
                                                    Primary
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-600">
                                            {maskAccountNumber(acc.accountNumber)} | {acc.ifscCode} | {acc.accountType.toUpperCase()}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {!acc.isPrimary && (
                                            <button
                                                onClick={() => handleSetPrimary(acc.id)}
                                                className="text-indigo-600 hover:text-indigo-800 text-xs font-semibold px-2 py-1 rounded border border-indigo-200 bg-indigo-50 transition"
                                            >
                                                Set Primary
                                            </button>
                                        )}
                                        {/* <button
                                            onClick={() => handleDelete(acc.id)}
                                            className="text-red-600 hover:text-red-800 text-xs font-semibold px-2 py-1 rounded border border-red-200 bg-red-50 transition"
                                        >
                                            Delete
                                        </button> */}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}